import React from 'react'
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { useAdminOverview } from '@hooks'
import { formatAdminDate, formatCount, formatEventPrice } from '@utils'
import type { AdminStackParamList } from '../../../navigation/navigator/AdminStackNavigator'
import { AdminPage } from './components/AdminPage'
import { AdminEventRow } from './components/AdminEventRow'
import { PersonAvatar } from './components/PersonAvatar'
import { StatCard } from './components/StatCard'

type Props = NativeStackScreenProps<AdminStackParamList, 'AdminHome'>

/** Landing of the administration: the platform's key figures, each leading to its list. */
export const AdminHome: React.FC<Props> = ({ navigation }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const { data, isLoading, isError } = useAdminOverview()
  const goBack = () => (navigation.getParent() as any)?.navigate('MenuMain')
  const money = (cents: number) => formatEventPrice(cents, currentLanguage, data?.ticketing.currency)
  const count = (value: number) => formatCount(value, currentLanguage)

  return (
    <AdminPage title={t('admin.title')} onBack={goBack} active="overview">
      {isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : isError || !data ? (
        <Text type="bodySmall" color={theme.colors.error} testID="admin-overview-error">
          {t('admin.overview.error')}
        </Text>
      ) : (
        <>
          <Text type="subtitleBold">{t('admin.overview.people')}</Text>
          <View style={styles.grid}>
            <StatCard
              testID="admin-stat-organizers"
              label={t('admin.overview.organizers')}
              value={count(data.organizers.active)}
              onPress={() => navigation.navigate('AdminOrganizers')}
            />
            <StatCard
              testID="admin-stat-requests"
              label={t('admin.overview.requests')}
              value={count(data.requests.pending)}
              color={data.requests.pending > 0 ? theme.colors.error : undefined}
              hint={t('admin.overview.requestsHint')}
              onPress={() => navigation.navigate('AdminRequests')}
            />
            <StatCard
              testID="admin-stat-attendees"
              label={t('admin.overview.attendees')}
              value={count(data.attendees.confirmed)}
              hint={t('admin.overview.attendeesHint')}
              onPress={() => navigation.navigate('AdminEvents')}
            />
            <StatCard
              testID="admin-stat-checkedin"
              label={t('admin.overview.checkedIn')}
              value={count(data.attendees.checkedIn)}
              hint={t('admin.overview.checkedInHint')}
              onPress={() => navigation.navigate('AdminEvents')}
            />
          </View>

          <Text type="subtitleBold">{t('admin.overview.eventsTitle')}</Text>
          <View style={styles.grid}>
            <StatCard
              testID="admin-stat-upcoming"
              label={t('admin.overview.upcoming')}
              value={count(data.events.upcoming)}
              color={theme.colors.primary}
              onPress={() => navigation.navigate('AdminEvents', { status: 'upcoming' })}
            />
            <StatCard
              testID="admin-stat-ongoing"
              label={t('admin.overview.ongoing')}
              value={count(data.events.ongoing)}
              color={theme.colors.green}
              onPress={() => navigation.navigate('AdminEvents', { status: 'ongoing' })}
            />
            <StatCard
              testID="admin-stat-past"
              label={t('admin.overview.past')}
              value={count(data.events.ended)}
              onPress={() => navigation.navigate('AdminEvents', { status: 'ended' })}
            />
          </View>

          <Text type="subtitleBold">{t('admin.overview.ticketingTitle')}</Text>
          <View style={styles.grid}>
            <StatCard
              testID="admin-stat-paid"
              label={t('admin.overview.paid')}
              value={money(data.ticketing.paidCents)}
              hint={t('admin.overview.paymentsCount', { count: data.ticketing.paymentsCount })}
              onPress={() => navigation.navigate('AdminEvents', { pricing: 'paid' })}
            />
            <StatCard
              testID="admin-stat-commission"
              label={t('admin.overview.commission')}
              value={money(data.ticketing.commissionCents)}
              color={theme.colors.primary}
              onPress={() => navigation.navigate('AdminEvents', { pricing: 'paid' })}
            />
            <StatCard
              testID="admin-stat-net"
              label={t('admin.overview.net')}
              value={money(data.ticketing.organizerNetCents)}
              hint={t('admin.overview.netHint')}
              onPress={() => navigation.navigate('AdminEvents', { pricing: 'paid' })}
            />
          </View>
          <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
            {t('admin.overview.paidOnly')}
          </Text>

          <View style={styles.sectionHead}>
            <Text type="subtitleBold">{t('admin.overview.nextEvents')}</Text>
            <Button
              title={t('admin.overview.seeAllEvents')}
              unfilled
              isSmall
              onPress={() => navigation.navigate('AdminEvents', { status: 'upcoming' })}
            />
          </View>
          {data.nextEvents.length === 0 ? (
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t('admin.overview.noNextEvents')}
            </Text>
          ) : (
            data.nextEvents.map((event) => (
              <AdminEventRow
                key={event.id}
                event={event}
                onPress={() => navigation.navigate('AdminEventDetail', { eventId: event.id })}
              />
            ))
          )}

          <View style={styles.sectionHead}>
            <Text type="subtitleBold">{t('admin.overview.latestRequests')}</Text>
            <Button
              title={t('admin.overview.seeRequests')}
              unfilled
              isSmall
              onPress={() => navigation.navigate('AdminRequests')}
            />
          </View>
          {data.latestRequests.length === 0 ? (
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t('admin.overview.noRequests')}
            </Text>
          ) : (
            data.latestRequests.map((request) => (
              <TouchableOpacity
                key={request.id}
                accessibilityRole="button"
                testID={`admin-latest-request-${request.id}`}
                style={styles.requestRow}
                onPress={() => navigation.navigate('AdminRequests')}
              >
                <PersonAvatar
                  uri={request.account.avatarUrl}
                  firstName={request.account.firstName}
                  lastName={request.account.lastName}
                  size={40}
                />
                <View style={styles.requestBody}>
                  <Text type="bodySmallBold" numberOfLines={1}>
                    {request.organizationName}
                  </Text>
                  <Text type="bodyExtraSmall" color={theme.colors.greyBlue} numberOfLines={1}>
                    {[
                      `${request.account.firstName} ${request.account.lastName}`.trim() ||
                        request.account.email,
                      formatAdminDate(request.createdAt, currentLanguage),
                    ].join(' · ')}
                  </Text>
                </View>
                <View style={[styles.status, { borderColor: statusColor(theme, request.status) }]}>
                  <Text type="bodyExtraSmallBold" color={statusColor(theme, request.status)}>
                    {t(`admin.overview.status.${request.status}`)}
                  </Text>
                </View>
              </TouchableOpacity>
            ))
          )}
        </>
      )}
    </AdminPage>
  )
}

const statusColor = (theme: Theme, status: 'pending' | 'approved' | 'rejected') =>
  status === 'approved' ? theme.colors.green : status === 'rejected' ? theme.colors.error : theme.colors.orange

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.md },
    sectionHead: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.md,
    },
    requestRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      padding: theme.spacing.md,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    requestBody: { flex: 1, minWidth: 0, gap: 2 },
    status: {
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: 2,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
    },
  })
